import type { Dispatch, FC, SetStateAction } from "react";
import { useLocale } from "hooks";
import Image from "components/Image";
import translation from "./translation";
import DateUtils from "utils/DateUtils";
import clsx from "clsx";
import styled from "styled-components";

interface IProps {
  item: any;
  index: number;
  onClick: Function;
  isActive: boolean;
  className?: string;
  activeMoreOption: Object | null;
  setActiveMoreOption: Dispatch<SetStateAction<Object | null>>;
  setActiveModalItem: Dispatch<SetStateAction<Object | null>>;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const SidebarCard: FC<IProps> = ({
  item,
  className,
  index,
  onClick,
  isActive,
  activeMoreOption,
  setActiveMoreOption,
  setActiveModalItem,
  setIsModalOpen,
}) => {
  const { isFA, locale, isRTL } = useLocale();

  const onShareButtonClick = () => {
    setIsModalOpen(true);
    setActiveModalItem(item);
  };

  const onMoreOptionsClick = (ev) => {
    ev.stopPropagation();
    setActiveMoreOption((prev) => (prev?.id === item?.id ? null : item));
  };

  // const onMouseLeave = () => {
  //   setActiveMoreOption(null);
  // };

  return (
    <div
      onClick={() => onClick(item)}
      key={item?.id}
      onMouseLeave={() => setActiveMoreOption(null)}
      className={clsx(
        "tw-flex tw-group tw-cursor-pointer tw-mb-2 last:tw-mb-0 hover:tw-bg-[#EFF2F9] tw-px-2.5 tw-py-2 tw-rounded-lg tw-relative",
        className,
        { "tw-bg-[#EFF2F9]": isActive }
      )}
    >
      <div className="tw-flex tw-items-center">
        <span className="tw-w-5">{index + 1}</span>
        <div className="tw-min-w-[122px] tw-max-w-[122px] xxxl:tw-min-w-[130px] xxxl:tw-max-w-[130px] tw-aspect-[1.8] tw-overflow-hidden tw-rounded-lg tw-relative">
          <Image
            width={122}
            height={68}
            src={item?.thumbnail}
            alt={item?.imageTag || item?.title || ""}
            className="tw-transition-all tw-w-full tw-h-full tw-object-cover"
          />
          <span className="tw-text-xs tw-absolute tw-bottom-2 tw-left-2 tw-text-white tw-bg-[black]/80 tw-px-1 tw-py-0.5 tw-rounded">
            {item?.duration}
          </span>
        </div>
      </div>
      <div
        className={clsx({
          "tw-pl-[50px] tw-mr-3": isRTL,
          "tw-pr-[50px] tw-ml-3": !isRTL,
        })}
      >
        <span
          className={clsx(
            "tw-text-[#344456] tw-text-sm xxxl:tw-text-base tw-font-bold group-hover:tw-text-[#007FAC] tw-transition-all",
            "title",
            { "tw-text-[#007FAC]": isActive }
          )}
        >
          {item?.title}
        </span>

        <span className="tw-text-[#344456] tw-text-[12px] xxxl:tw-text-sm tw-font-bold tw-mt-1 xxxl:tw-mt-3 group-hover:tw-text-[#007FAC] tw-transition-all title">
          {item?.publishDate}
        </span>

        <p className="tw-text-[#344456]/40 tw-text-[13px]">
          <span>
            {isFA
              ? DateUtils.parsePersianhNewsDate(item?.publish_date)
              : DateUtils.parseEnglishNewsDate(item?.publish_date)}
          </span>
        </p>

        <div
          className={clsx("tw-absolute tw-top-8", {
            "tw-left-1 ": isRTL,
            "tw-right-1": !isRTL,
            " tw-z-10": activeMoreOption?.id === item?.id,
            " tw-z-[9]": activeMoreOption?.id !== item?.id,
          })}
        >
          <div
            className="tw-bg-zero tw-border-0 tw-cursor-pointer tw-group"
            onClick={onMoreOptionsClick}
            // onMouseLeave={onMouseLeave}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.9984 16H11.0074"
                stroke="#201F24"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.9945 16H16.0035"
                stroke="#201F24"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.9945 16H21.0035"
                stroke="#201F24"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <rect
                x="0.5"
                y="0.5"
                width="31"
                height="31"
                rx="15.5"
                stroke="black"
                strokeOpacity="0.1"
              />
            </svg>
            {activeMoreOption?.id === item?.id && (
              <div
                className={clsx(
                  "tw-bg-white tw-rounded-xl tw-p-2 tw-absolute tw-w-[176px]",
                  {
                    "tw-left-0": isRTL,
                    "tw-right-0": !isRTL,
                  }
                )}
              >
                {item?.src && (
                  <a
                    download
                    target="_blank"
                    href={item?.src}
                    className="tw-bg-zero tw-border-0 tw-px-4 tw-py-2 tw-block tw-text-[13px] tw-cursor-pointer hover:tw-bg-gray-9 tw-w-full tw-text-start"
                  >
                    {translation[locale]?.DOWNLOAD}
                  </a>
                )}
                <div
                  onClick={() => onShareButtonClick()}
                  className="tw-bg-zero tw-border-0 tw-px-4 tw-py-2 tw-block tw-text-[13px] tw-cursor-pointer hover:tw-bg-gray-9 tw-w-full tw-text-start"
                >
                  {translation[locale]?.SHARE}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledSidebarCard = styled(SidebarCard)`
  .title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  img {
    transition: transform 0.3s;
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;

export default StyledSidebarCard;
